import React, {
    useState,
    useEffect,
    useReducer,
    ChangeEvent,
    useMemo,
} from 'react';
import parse from 'html-react-parser';
import { Dropdown } from '../../../../common';
import {
    PERSONAL_DETAIL_STEP_KEYS,
    STEP_PROGRESS_BAR_KEYS,
    LIGHT_JOURNEY_ANALYTICS,
    OSB_CLIENT_STORAGE_KEYS,
} from '../../osb-constant';
import './light-personal-details-flow-lincoln.scss';
import {
    usePersonalDetailStep,
    useHttp,
    getInitialPersonalDetailState,
    useVehicleStep,
    useViewport,
    useStepProgressBar,
    useOSBStep,
    usePageRefresh,
    useDealerStep,
} from '../../../../../hooks/owners-osb';
import {
    PersonalDetail,
    PersonalDetailErrorField,
} from '../../../../../models/osb-model/osb-personal-details';
import errorWarning from '../../../../../assets/error-warning.svg';
import { personalDetailReducer } from '../../../../../reducers/osb-reducers';
import { LightBookingSummary } from '../../light-booking-summary/light-booking-summary';
import BookingSummaryAccordion from '../../light-booking-summary/booking-summary-accordion/booking-summary-accordion';
import { OSBSecondaryButton } from '../../common/osb-secondary-button/osb-secondary-button';
import { useAnalytics } from '../../../../../hooks/use-analytics';
import {
    OsbPathBookingRetrieveSummaryStep,
    OsbPathBookingReviewStep,
    OsbPathCalenderStep,
    OsbPathPersonalDetailsStep,
} from '../../osb-controller';
import {
    triggerDealerContactDetailsAnalytics,
    triggerContactDetailsErrorAnalytics,
    triggerRetrieveAmendContactDetailsLoadAnalytics,
    triggerRetrieveAmendContactDetailsErrorAnalytics,
    triggerBookServiceGlobalCTAOnClickAnalytics,
} from '../../analytics/osb-analytics';
import ServiceHandler from '../../../../../services/service-handler';
import {
    setRouteToPersist,
    buildNavigationUrl,
    formatRegVinNumber,
    getClientStorageValue,
    getGoMainHeaderHeight,
} from '../../osb-utils';
import { useHistory } from 'react-router-dom';
import { OsbLoader } from '../../../../common/osb-loader/osb-loader';
import { VehicleData } from '../../../../../models/osb-model/osb-vehicle-step';
import CountryDialCodePicker, {
    CountryDialCodePickerContent,
    CountryDialCodePickerOptions,
} from '../../../../common/country-dial-code-picker/country-dial-code-picker';
import { useCountryDialCodePicker } from '../../../../../hooks/use-country-dial-code-picker';
import OsbUtilService from '../../../../../services/osb-service/osb-util-service/osb-util-service';
import serverSideService from '../../../../../services/server-side-service/server-side-service';
import useDealerCalendarAvailability from '../../../../../hooks/owners-osb/use-dealer-calendar-availability';
import { useAllOSBState } from '../../../../../hooks/owners-osb/use-all-osb-state';
import { FMButton } from '@own/fds-react';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';

// Reusable FormInput component
interface FormInputProps {
    id: string;
    'data-testid': string;
    containerClassName: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
    onBlur: () => void;
    errorMsg: string;
    label: string;
    placeholder: string;
    isLabelVisible: boolean;
    type?: string;
    maxLength?: number;
}

const FormInput = ({
    id,
    'data-testid': dataTestId,
    containerClassName,
    value,
    onChange,
    onFocus,
    onBlur,
    errorMsg,
    label,
    placeholder,
    isLabelVisible,
    type = 'text',
    maxLength,
}: FormInputProps) => (
    <div className={containerClassName}>
        <label className="input-label">
            {(isLabelVisible || (value && value.length > 0)) && (
                <span>{label}</span>
            )}
            {errorMsg ? (
                <img className="osb-error-icon" src={errorWarning} alt="" />
            ) : (
                <span />
            )}
        </label>
        <input
            id={id}
            data-testid={dataTestId}
            type={type}
            className={`input-field${errorMsg ? '-error' : ''}`}
            placeholder={placeholder}
            aria-label={id}
            aria-labelledby={id}
            name={id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            maxLength={maxLength}
        />
        {errorMsg ? (
            <div className="osb-error-message">{errorMsg}</div>
        ) : (
            <span />
        )}
    </div>
);

interface Props {
    osbAlertMessagePopup: (alertMessage: string, alertSuccess: boolean) => void;
}
export const LightPersonalDetailsFlow = (props: Props) => {
    enum PersonalDetailsConsent {
        FIRST_CONSENT = 1,
        SECOND_CONSENT = 2,
    }
    const { fetchAvailability } = useDealerCalendarAvailability();
    const {
        osbStepProgressBar,
        updateProgressBarStatus,
    } = useStepProgressBar();
    const { osbVehicleStep, setOSBVehicleStepPayload } = useVehicleStep();
    const { isMobileView } = useViewport();
    const { osbDealerStep } = useDealerStep();
    const bookable = useAllOSBState();
    const [fireEvents] = useAnalytics();

    const personalDetailsJson = useOSBContentFragment(
        'common/owners-osb',
        'osb-personal-details-step'
    );
    const [firstNameErrorMsg, setFirstNameErrorMsg] = useState('');
    const [lastNameErrorMsg, setLastNameErrorMsg] = useState('');
    const [contactNumberErrorMsg, setContactNumberErrorMsg] = useState('');
    const [emailErrorMsg, setEmailErrorMsg] = useState('');
    const [isFirstConsentDisabled, setIsFirstConsentDisabled] = useState<
        boolean
    >(false);
    const [isSecondConsentDisabled, setIsSecondConsentDisabled] = useState<
        boolean
    >(false);
    const [
        isregistrationNumberLabelShow,
        setIsregistrationNumberLabelShow,
    ] = useState<boolean>(false);
    const [isEmailLabelShow, setIsEmailLabelShow] = useState<boolean>(false);
    const [isContactNumberLabelShow, setIsContactNumberLabelShow] = useState<
        boolean
    >(false);
    const [isLastNameLabelShow, setIsLastNameLabelShow] = useState<boolean>(
        false
    );
    const [isFirstNameLabelShow, setIsFirstNameLabelShow] = useState<boolean>(
        false
    );
    const invalidFormFieldsForAnalytic: PersonalDetailErrorField[] = [];
    const [
        registrationNumberErrorMsg,
        setRegistrationNumberErrorMsg,
    ] = useState('');
    const { osbPersonalDetail, setOSBPersonalDetail } = usePersonalDetailStep();
    const [personalDetail, setPersonalDetail] = useReducer(
        personalDetailReducer,
        getInitialPersonalDetailState()
    );
    const { httpState, dispatch } = useHttp();
    const { osbStep, invalidateAuthentication } = useOSBStep();
    const history = useHistory();
    const isRetrieveStep = osbStep.isRetrieveFlow;

    const [showSaveToMyFordCheckbox, setShowSaveToMyFordCheckbox] = useState<
        boolean
    >(false);
    const [saveToMyFordCheckboxValue, setSaveToMyFordCheckboxValue] = useState<
        boolean
    >(false);

    const [isDirtyTitle, setIsDirtyTitle] = useState<boolean>(false);
    const [isDirtyFirstName, setIsDirtyFirstName] = useState<boolean>(false);
    const [isDirtyLastName, setIsDirtyLastName] = useState<boolean>(false);
    const [isDirtyContactNumber, setIsDirtyContactNumber] = useState<boolean>(
        false
    );
    const countryDialCodeContent = useCountryDialCodePicker();
    const displayCountryDialCodeComponent = true;
    const appConfig = OsbUtilService.getAppConfiguration();
    const [countryCodeData, setCountryCodeData] = useState<
        CountryDialCodePickerContent
    >();
    const [
        defaultMarketCodeToDisplay,
        setDefaultMarketCodeToDisplay,
    ] = useState<string>('');

    const [
        disableReviewBookingButtonValue,
        setDisableReviewBookingButtonValue,
    ] = useState<boolean>(true);
    usePageRefresh();

    const dropdownOptions = useMemo(() => {
        const rawOptions =
            personalDetailsJson?.[PERSONAL_DETAIL_STEP_KEYS.OPTIONS];

        if (Array.isArray(rawOptions)) {
            return rawOptions;
        }
        return [];
    }, [personalDetailsJson]);

    const personalDetailsJsonString = useMemo(
        () => JSON.stringify(personalDetailsJson),
        [personalDetailsJson]
    );

    const setComponentPayload = (
        payload:
            | { [key: string]: string | boolean | CountryDialCodePickerOptions }
            | PersonalDetail
    ): void => {
        setPersonalDetail({
            type: 'SET',
            payload: payload,
            localStorageExpiry: osbStep.localStorageExpiry,
        });
    };
    const goToDateTimeStep = () => {
        triggerBookServiceGlobalCTAOnClickAnalytics(
            LIGHT_JOURNEY_ANALYTICS.BACK_BTN_CTA_NAME,
            fireEvents,
            osbVehicleStep
        );
        invalidateAuthentication();
        updateProgressBarStatus(STEP_PROGRESS_BAR_KEYS.CONTACT, false);
        history.push(
            buildNavigationUrl(OsbPathCalenderStep(), osbStep.UrlQueryParams)
        );
    };

    const loadVoucherAlertMessage = (message: string, success: boolean) => {
        props.osbAlertMessagePopup(message, success);
    };

    function isPrivacyAnchorTagPresent(consentId: PersonalDetailsConsent) {
        if (!personalDetailsJson) return false;

        if (PersonalDetailsConsent.FIRST_CONSENT === consentId) {
            const consentLinkText =
                personalDetailsJson[
                    PERSONAL_DETAIL_STEP_KEYS.FIRST_CONSENT_LINK_TEXT
                ];
            const targetUrl =
                personalDetailsJson[
                    PERSONAL_DETAIL_STEP_KEYS.FIRST_CONSENT_PRIVACY_TARGET_URL
                ];
            return !!(targetUrl && String(consentLinkText || '').trim());
        } else if (PersonalDetailsConsent.SECOND_CONSENT === consentId) {
            const consentLinkText =
                personalDetailsJson[
                    PERSONAL_DETAIL_STEP_KEYS.SECOND_CONSENT_LINK_TEXT
                ];
            const targetUrl =
                personalDetailsJson[
                    PERSONAL_DETAIL_STEP_KEYS.SECOND_CONSENT_PRIVACY_TARGET_URL
                ];
            return !!(targetUrl && String(consentLinkText || '').trim());
        }
        return false;
    }
    const isShowSaveToMyFordCheckbox = (
        titleDirty: boolean,
        firstNameDirty: boolean,
        lastNameDirty: boolean,
        contactNumDirty: boolean
    ) => {
        const saveToMyFordLabel =
            personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.SAVE_TO_MY_FORD_LABEL
            ];
        if (
            (titleDirty ||
                firstNameDirty ||
                lastNameDirty ||
                contactNumDirty) &&
            saveToMyFordLabel
        ) {
            setShowSaveToMyFordCheckbox(true);
        } else {
            setShowSaveToMyFordCheckbox(false);
        }
    };
    const onTitleChange = (label: string) => {
        const titleDefaultLabel =
            personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.AUTOFILL_TITLE_LABEL
            ];

        if (
            label?.toUpperCase() !==
            String(titleDefaultLabel || '').toUpperCase()
        ) {
            setComponentPayload({ title: label || '' });
        }
        if (osbStep.isAuthenticatedFlow) {
            if (osbPersonalDetail.title !== label) {
                setIsDirtyTitle(true);
            } else {
                setIsDirtyTitle(false);
            }
            isShowSaveToMyFordCheckbox(
                osbPersonalDetail.title !== label,
                isDirtyFirstName,
                isDirtyLastName,
                isDirtyContactNumber
            );
        }
    };
    const handleFirstNameValidation = (value: string) => {
        const regexPattern = String(
            personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.FIRST_NAME_REGEX_PATTERN
            ] || ''
        );
        const regex = new RegExp(regexPattern);
        if (
            (personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.FIRST_NAME_MANDATORY_FIELD
            ] &&
                value.length <= 0) ||
            (value.length > 0 && regex.exec(value) === null)
        ) {
            setFirstNameErrorMsg(
                String(
                    personalDetailsJson?.[
                        PERSONAL_DETAIL_STEP_KEYS.FIRST_NAME_ERROR_MESSAGE
                    ] || ''
                )
            );
            const inputValue = value.length <= 0 ? 'no entry' : 'invalid entry';
            invalidFormFieldsForAnalytic.push({
                field: 'firstName',
                entry: inputValue,
            });
            return false;
        } else {
            setFirstNameErrorMsg('');
            return true;
        }
    };
    const isAEMConsentEnabled = (consentNumber: number) => {
        if (!personalDetailsJson) return false;
        if (consentNumber === PersonalDetailsConsent.FIRST_CONSENT)
            return personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.ENABLE_FIRST_CONSENT_CHECKBOX
            ];
        else if (consentNumber === PersonalDetailsConsent.SECOND_CONSENT)
            return personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.ENABLE_SECOND_CONSENT_CHECKBOX
            ];
        else return false;
    };
    const enableReviewBookingButton = () => {
        if (!personalDetailsJson) {
            return false;
        }
        if (
            personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.FIRST_NAME_MANDATORY_FIELD
            ] &&
            (personalDetail.firstName === '' || firstNameErrorMsg !== '')
        ) {
            return false;
        }
        if (
            personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.LAST_NAME_MANDATORY_FIELD
            ] &&
            (personalDetail.lastName === '' || lastNameErrorMsg !== '')
        ) {
            return false;
        }
        if (
            personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.CONTACT_NUMBER_MANDATORY_FIELD
            ] &&
            (personalDetail.contactNumber === '' ||
                contactNumberErrorMsg !== '')
        ) {
            return false;
        }
        if (
            personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.EMAIL_MANDATORY_FIELD
            ] &&
            (personalDetail.email === '' || emailErrorMsg !== '')
        ) {
            return false;
        }
        const isVinMissing =
            osbVehicleStep.vehicleDetails.vin === '' ||
            osbVehicleStep.vehicleDetails.vin === null;
        if (
            personalDetailsJson[
                PERSONAL_DETAIL_STEP_KEYS.REG_NUMBER_MANDATORY_FIELD
            ] &&
            isVinMissing &&
            (personalDetail.registrationNumber === '' ||
                registrationNumberErrorMsg !== '')
        ) {
            return false;
        }
        if (
            isAEMConsentEnabled(PersonalDetailsConsent.FIRST_CONSENT) &&
            !personalDetail.isFirstConsentEnabled
        ) {
            return false;
        }
        if (
            isAEMConsentEnabled(PersonalDetailsConsent.SECOND_CONSENT) &&
            !personalDetail.isSecondConsentEnabled
        ) {
            return false;
        }
        return true;
    };

    const handleLastNameValidation = (value: string) => {
        const regexPattern = String(
            personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.LAST_NAME_REGEX_PATTERN
            ] || ''
        );
        const regex = new RegExp(regexPattern);
        if (
            (personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.LAST_NAME_MANDATORY_FIELD
            ] &&
                value.length <= 0) ||
            (value.length > 0 && regex.exec(value) === null)
        ) {
            setLastNameErrorMsg(
                String(
                    personalDetailsJson?.[
                        PERSONAL_DETAIL_STEP_KEYS.LAST_NAME_ERROR_MESSAGE
                    ] || ''
                )
            );
            const inputValue = value.length <= 0 ? 'no entry' : 'invalid entry';
            invalidFormFieldsForAnalytic.push({
                field: 'lastName',
                entry: inputValue,
            });
            return false;
        } else {
            setLastNameErrorMsg('');
            return true;
        }
    };

    const handleContactNumberValidation = (
        value: string,
        regexPattern?: string
    ) => {
        const finalRegexPattern = String(
            (regexPattern && regexPattern.length > 0
                ? regexPattern
                : personalDetailsJson?.[
                      PERSONAL_DETAIL_STEP_KEYS.CONTACT_NUMBER_REGEX_PATTERN
                  ]) || ''
        );
        const regex = new RegExp(finalRegexPattern);
        if (
            (personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.CONTACT_NUMBER_MANDATORY_FIELD
            ] &&
                value.length <= 0) ||
            (value.length > 0 && regex.exec(value) === null)
        ) {
            setContactNumberErrorMsg(
                String(
                    personalDetailsJson?.[
                        PERSONAL_DETAIL_STEP_KEYS.CONTACT_NUMBER_ERROR_MESSAGE
                    ] || ''
                )
            );
            const inputValue = value.length <= 0 ? 'no entry' : 'invalid entry';
            invalidFormFieldsForAnalytic.push({
                field: 'contactNumber',
                entry: inputValue,
            });
            return false;
        } else {
            setContactNumberErrorMsg('');
            return true;
        }
    };
    const handleEmailValidation = (value: string) => {
        const regexPattern = String(
            personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.EMAIL_REGEX_PATTERN
            ] || ''
        );
        const regex = new RegExp(regexPattern);
        if (
            (personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.EMAIL_MANDATORY_FIELD
            ] &&
                value.length <= 0) ||
            (value.length > 0 && regex.exec(value) === null)
        ) {
            setEmailErrorMsg(
                String(
                    personalDetailsJson?.[
                        PERSONAL_DETAIL_STEP_KEYS.EMAIL_ERROR_MESSAGE
                    ] || ''
                )
            );
            const inputValue = value.length <= 0 ? 'no entry' : 'invalid entry';
            invalidFormFieldsForAnalytic.push({
                field: 'email',
                entry: inputValue,
            });
            return false;
        } else {
            setEmailErrorMsg('');
            return true;
        }
    };
    const handleRegistrationNumberValidation = (value: string) => {
        const regexPattern = String(
            personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.REG_NUMBER_REGEX_PATTERN
            ] || ''
        );
        const regex = new RegExp(regexPattern);
        if (
            (personalDetailsJson?.[
                PERSONAL_DETAIL_STEP_KEYS.REG_NUMBER_MANDATORY_FIELD
            ] &&
                value.length <= 0) ||
            (value.length > 0 && regex.exec(value) === null)
        ) {
            setRegistrationNumberErrorMsg(
                String(
                    personalDetailsJson?.[
                        PERSONAL_DETAIL_STEP_KEYS.REG_NUMBER_ERROR_MESSAGE
                    ] || ''
                )
            );
            const inputValue = value.length <= 0 ? 'no entry' : 'invalid entry';
            invalidFormFieldsForAnalytic.push({
                field: 'registrationNumber',
                entry: inputValue,
            });
            return false;
        } else {
            setRegistrationNumberErrorMsg('');
            return true;
        }
    };

    const onFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (osbStep.isAuthenticatedFlow) {
            if (osbPersonalDetail.firstName !== e.target.value) {
                setIsDirtyFirstName(true);
            } else {
                setIsDirtyFirstName(false);
            }
            isShowSaveToMyFordCheckbox(
                isDirtyTitle,
                osbPersonalDetail.firstName !== e.target.value,
                isDirtyLastName,
                isDirtyContactNumber
            );
        }
        setComponentPayload({ firstName: e.target.value.trimStart() });
        handleFirstNameValidation(e.target.value.trimStart());
    };
    const onLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (osbStep.isAuthenticatedFlow) {
            if (osbPersonalDetail.lastName !== e.target.value) {
                setIsDirtyLastName(true);
            } else {
                setIsDirtyLastName(false);
            }
            isShowSaveToMyFordCheckbox(
                isDirtyTitle,
                isDirtyFirstName,
                osbPersonalDetail.lastName !== e.target.value,
                isDirtyContactNumber
            );
        }
        setComponentPayload({ lastName: e.target.value.trimStart() });
        handleLastNameValidation(e.target.value.trimStart());
    };
    const onContactNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (osbStep.isAuthenticatedFlow) {
            if (osbPersonalDetail.contactNumber !== e.target.value) {
                setIsDirtyContactNumber(true);
            } else {
                setIsDirtyContactNumber(false);
            }
            isShowSaveToMyFordCheckbox(
                isDirtyTitle,
                isDirtyFirstName,
                isDirtyLastName,
                osbPersonalDetail.contactNumber !== e.target.value
            );
        }
        if (displayCountryDialCodeComponent) {
            let value = e.target.value.trim();
            if (
                !value.includes(
                    personalDetail.countryDialCodeObject.countryDialCode
                )
            ) {
                value = `${personalDetail.countryDialCodeObject.countryDialCode}`;
            }
            setComponentPayload({
                contactNumber: `${value}`,
            });
            handleContactNumberValidation(
                e.target.value.trimStart(),
                personalDetail.countryDialCodeObject.countryRegex
            );
        } else {
            setComponentPayload({ contactNumber: e.target.value.trimStart() });
            handleContactNumberValidation(e.target.value.trimStart());
        }
    };
    const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setComponentPayload({ email: e.target.value.trimStart() });
        handleEmailValidation(e.target.value.trimStart());
    };
    const onRegistrationNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        setComponentPayload({ registrationNumber: e.target.value.trimStart() });
        handleRegistrationNumberValidation(
            e.target.value.toUpperCase().trimStart()
        );
    };

    const registrationNumberLabelShow = () => {
        setIsregistrationNumberLabelShow(true);
    };

    const registrationNumberLabelHide = () => {
        setIsregistrationNumberLabelShow(false);
    };

    const emailLabelShow = () => {
        setIsEmailLabelShow(true);
    };

    const emailLabelHide = () => {
        setIsEmailLabelShow(false);
    };

    const contactNumberLabelShow = () => {
        setIsContactNumberLabelShow(true);
    };

    const contactNumberLabelHide = () => {
        setIsContactNumberLabelShow(false);
    };

    const lastNameLabelShow = () => {
        setIsLastNameLabelShow(true);
    };

    const lastNameLabelHide = () => {
        setIsLastNameLabelShow(false);
    };

    const firstNameLabelShow = () => {
        setIsFirstNameLabelShow(true);
    };

    const firstNameLabelHide = () => {
        setIsFirstNameLabelShow(false);
    };
    function handleConsentClick(consentId: PersonalDetailsConsent) {
        if (PersonalDetailsConsent.FIRST_CONSENT === consentId) {
            setIsFirstConsentDisabled(false);
        }
        if (PersonalDetailsConsent.SECOND_CONSENT === consentId) {
            setIsSecondConsentDisabled(false);
        }
    }
    const createPersonalDetailsStep = () => {
        const vehicleDetails = osbVehicleStep.vehicleDetails;
        if (
            vehicleDetails.vin !== '' &&
            personalDetail?.registrationNumber?.length > 0
        ) {
            vehicleDetails.registrationNumber =
                personalDetail.registrationNumber;
            setOSBVehicleStepPayload({ vehicleDetails: vehicleDetails }, false);
        }
    };
    const handleVinRegFieldData = () => {
        if (osbVehicleStep.vehicleDetails.vin === '') {
            const isRegValid = handleRegistrationNumberValidation(
                personalDetail.registrationNumber
            );
            return isRegValid;
        } else {
            return true;
        }
    };
    const validatePersonalDetails = () => {
        const isValidFn = handleFirstNameValidation(personalDetail.firstName);
        const isValidLn = handleLastNameValidation(personalDetail.lastName);
        const isValidCn = displayCountryDialCodeComponent
            ? handleContactNumberValidation(
                  personalDetail.contactNumber,
                  personalDetail.countryDialCodeObject.countryRegex
              )
            : handleContactNumberValidation(personalDetail.contactNumber);
        const isValidEmail = handleEmailValidation(personalDetail.email);
        const isValidRn = handleVinRegFieldData();
        if (
            !isValidFn ||
            !isValidLn ||
            !isValidCn ||
            !isValidEmail ||
            !isValidRn
        ) {
            if (!isRetrieveStep) {
                triggerContactDetailsErrorAnalytics(
                    invalidFormFieldsForAnalytic,
                    osbVehicleStep,
                    osbStep,
                    fireEvents,
                    LIGHT_JOURNEY_ANALYTICS.NO_AMEND_FLOW_CONTENT
                );
            }
            if (isRetrieveStep) {
                triggerRetrieveAmendContactDetailsErrorAnalytics(
                    invalidFormFieldsForAnalytic,
                    osbVehicleStep,
                    osbStep,
                    fireEvents,
                    LIGHT_JOURNEY_ANALYTICS.AMEND_FLOW_CONTENT
                );
            }
        }

        if (isValidFn && isValidLn && isValidCn && isValidEmail && isValidRn) {
            return true;
        }
        return false;
    };
    const updateFordAccount = async () => {
        const payload: any = {};
        if (osbPersonalDetail.title !== personalDetail.title) {
            payload['title'] = personalDetail.title;
        }
        if (osbPersonalDetail.firstName !== personalDetail.firstName) {
            payload['firstName'] = personalDetail.firstName;
        }
        if (osbPersonalDetail.lastName !== personalDetail.lastName) {
            payload['lastName'] = personalDetail.lastName;
        }
        if (osbPersonalDetail.contactNumber !== personalDetail.contactNumber) {
            payload['phoneNumber'] = personalDetail.contactNumber;
        }
        await ServiceHandler.FordAccountService.updateUserPersonalInfo(payload);
    };
    const goToReviewBookingStep = () => {
        invalidateAuthentication();
        const isValid = validatePersonalDetails();
        if (isValid) {
            triggerBookServiceGlobalCTAOnClickAnalytics(
                LIGHT_JOURNEY_ANALYTICS.REVIEW_BOOKING_BTN_CTA_NAME,
                fireEvents,
                osbVehicleStep
            );
            if (personalDetail.registrationNumber) {
                /*calls a vehicle lookup async api call to validate reg no */
                const vehicleData: VehicleData = {
                    registrationNumber: formatRegVinNumber(
                        personalDetail.registrationNumber,
                        osbVehicleStep.allowSpecialCharacters
                    ),
                    mileage: osbVehicleStep.defaultMileage,
                };
                ServiceHandler.OsbVehicleLookUp.postVehicleInfo({
                    vehicleData: vehicleData,
                    prognosticsData: osbVehicleStep.vehiclePrognosticData,
                    dealerCode: osbDealerStep.selectedDealerProfile.dealerCode,
                    bookable: bookable,
                })
                    .then()
                    .catch((error: any) => {
                        dispatch({
                            type: 'ERROR',
                            error: error.errorMessage,
                        });
                    });
            }
            createPersonalDetailsStep();
            setOSBPersonalDetail(personalDetail, false);
            if (osbStep.isAuthenticatedFlow && saveToMyFordCheckboxValue) {
                // update personal details value to my ford account
                updateFordAccount();
            }
            updateProgressBarStatus(STEP_PROGRESS_BAR_KEYS.CONTACT, true);
            if (osbStep.isRetrieveFlow) {
                history.push(
                    buildNavigationUrl(
                        OsbPathBookingRetrieveSummaryStep(),
                        osbStep.UrlQueryParams
                    )
                );
            } else {
                setRouteToPersist(
                    OSB_CLIENT_STORAGE_KEYS.OSB_PERSISTED_ROUTE_KEY,
                    OsbPathBookingReviewStep(),
                    osbStep.localStorageExpiry
                );
                history.push(
                    buildNavigationUrl(
                        OsbPathBookingReviewStep(),
                        osbStep.UrlQueryParams
                    )
                );
            }
        }
    };

    function selectedCountry(
        countrySelected: CountryDialCodePickerOptions | undefined,
        initialLoad: boolean
    ) {
        if (countrySelected) {
            if (personalDetail.contactNumber.length > 0 && initialLoad) {
                setComponentPayload({
                    contactNumber: personalDetail.contactNumber.startsWith(
                        countrySelected.countryDialCode
                    )
                        ? personalDetail.contactNumber
                        : `${countrySelected.countryDialCode}${personalDetail.contactNumber}`,
                });
            } else {
                setComponentPayload({
                    contactNumber: countrySelected.countryDialCode,
                });
            }
            setComponentPayload({
                countryDialCodeObject: countrySelected,
            });
        }
    }

    function getCountryCodeFromContactNumber(contactNumber: string) {
        let countryCode = '';
        if (countryDialCodeContent?.dropdownOptions) {
            for (const item of countryDialCodeContent?.dropdownOptions || []) {
                if (contactNumber.startsWith(item.countryDialCode)) {
                    countryCode = item.countryCode;
                    break;
                }
            }
        }
        return countryCode;
    }

    useEffect(() => {
        if (countryDialCodeContent?.dropdownOptions) {
            if (osbPersonalDetail.contactNumber.length > 0) {
                const countryCode = getCountryCodeFromContactNumber(
                    osbPersonalDetail.contactNumber
                );
                if (countryCode.length > 0) {
                    setDefaultMarketCodeToDisplay(countryCode);
                } else {
                    setDefaultMarketCodeToDisplay(appConfig.countryCode);
                }
            } else {
                setDefaultMarketCodeToDisplay(appConfig.countryCode);
            }
            setCountryCodeData(countryDialCodeContent);
        }
    }, [countryDialCodeContent]);

    useEffect(() => {
        fetchAvailability();
    }, []);

    useEffect(() => {
        updateProgressBarStatus(STEP_PROGRESS_BAR_KEYS.CONTACT, false, true);
        const stepDetails = osbStepProgressBar.progressBarDetails.find(
            step =>
                step.key === STEP_PROGRESS_BAR_KEYS.CONTACT && step.isEditable
        );
        if (stepDetails || osbStep.isRetrieveFlow) {
            setRouteToPersist(
                OSB_CLIENT_STORAGE_KEYS.OSB_PERSISTED_ROUTE_KEY,
                OsbPathPersonalDetailsStep(),
                osbStep.localStorageExpiry
            );
            dispatch({ type: 'REQUEST' });
            setComponentPayload(
                getClientStorageValue(
                    OSB_CLIENT_STORAGE_KEYS.OSB_PERSONAL_DETAIL_STEP_KEY
                ) ?? osbPersonalDetail
            );
            setComponentPayload({
                isFirstConsentEnabled: false,
                isSecondConsentEnabled: false,
            });
            if (
                osbVehicleStep.vehicleStepFlow === 'manual' &&
                !osbVehicleStep.vehicleDetails.vin
            ) {
                setComponentPayload({
                    registrationNumber:
                        osbPersonalDetail?.registrationNumber ||
                        osbVehicleStep.vehicleDetails.registrationNumber ||
                        '',
                });
            }

            if (!isRetrieveStep) {
                triggerDealerContactDetailsAnalytics(
                    osbVehicleStep,
                    osbStep,
                    fireEvents,
                    LIGHT_JOURNEY_ANALYTICS.NO_AMEND_FLOW_CONTENT
                );
            }
            if (isRetrieveStep) {
                triggerRetrieveAmendContactDetailsLoadAnalytics(
                    osbVehicleStep,
                    osbStep,
                    fireEvents,
                    LIGHT_JOURNEY_ANALYTICS.AMEND_FLOW_CONTENT
                );
            }
            if (serverSideService.isClientSide()) {
                window.scrollTo({
                    top: getGoMainHeaderHeight() - 2,
                    behavior: 'smooth',
                });
            }
        }
    }, []);

    useEffect(() => {
        if (personalDetailsJson) {
            dispatch({ type: 'RESPONSE' });
            if (
                isPrivacyAnchorTagPresent(PersonalDetailsConsent.FIRST_CONSENT)
            ) {
                setIsFirstConsentDisabled(true);
            }
            if (
                isPrivacyAnchorTagPresent(PersonalDetailsConsent.SECOND_CONSENT)
            ) {
                setIsSecondConsentDisabled(true);
            }
        }
    }, [personalDetailsJsonString]);

    useEffect(() => {
        if (personalDetailsJson) {
            setDisableReviewBookingButtonValue(!enableReviewBookingButton());
        }
    }, [
        personalDetail.firstName,
        personalDetail.lastName,
        personalDetail.contactNumber,
        personalDetail.email,
        personalDetail.registrationNumber,
        personalDetail.isFirstConsentEnabled,
        personalDetail.isSecondConsentEnabled,
        personalDetailsJsonString,
    ]);

    const getSubHeading = (): JSX.Element | null => {
        const text = String(personalDetailsJson?.['text'] || '');
        if (!text) return null;
        return (
            <div className="personal-details-flow-sub-header">
                {parse(text)}
            </div>
        );
    };
    const handleOsbSaveToMyFordChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        setSaveToMyFordCheckboxValue(event.currentTarget.checked);
    };

    return (
        <>
            {httpState.isLoading || !personalDetailsJson ? (
                <OsbLoader osbEmptyContentHeight={true} />
            ) : (
                <>
                    <div className="osb-light-journey-personal-details-page">
                        <div className="osb-personal-details-section">
                            <div className="osb-light-journey-personal-details-flow-container">
                                {(osbStep.isWebViewJourney || !isMobileView) &&
                                    !isRetrieveStep && (
                                        <OSBSecondaryButton
                                            dataTestId="BackLinkToDataAndTimePage"
                                            type="svg"
                                            direction="left"
                                            text={String(
                                                personalDetailsJson[
                                                    PERSONAL_DETAIL_STEP_KEYS
                                                        .BACK_BUTTON
                                                ] || ''
                                            )}
                                            onClickHandler={goToDateTimeStep}
                                        />
                                    )}

                                <div className="personal-details-flow-header">
                                    {parse(
                                        String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .HEADING
                                            ] || ''
                                        )
                                    )}
                                </div>

                                <div className="osb-personal-details-form">
                                    <div className="osb-personal-details-title">
                                        <Dropdown
                                            dataTestId="PersonTitleDropDown"
                                            label={String(
                                                personalDetailsJson[
                                                    PERSONAL_DETAIL_STEP_KEYS
                                                        .AUTOFILL_TITLE_LABEL
                                                ] || ''
                                            )}
                                            options={dropdownOptions}
                                            value={personalDetail.title}
                                            onChange={e => onTitleChange(e)}
                                        />
                                    </div>
                                    <br></br>

                                    <FormInput
                                        id="firstName"
                                        data-testid="EnterFirstName"
                                        containerClassName="osb-personal-details-first-name"
                                        label={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .FIRST_NAME_LABEL
                                            ] || ''
                                        )}
                                        placeholder={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .FIRST_NAME_PLACEHOLDER
                                            ] || ''
                                        )}
                                        value={personalDetail.firstName}
                                        onChange={onFirstNameChange}
                                        onBlur={firstNameLabelHide}
                                        onFocus={firstNameLabelShow}
                                        errorMsg={firstNameErrorMsg}
                                        isLabelVisible={isFirstNameLabelShow}
                                    />
                                    <br></br>
                                    <FormInput
                                        id="lastName"
                                        data-testid="EnterLastName"
                                        containerClassName="osb-personal-details-last-name"
                                        label={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .LAST_NAME_LABEL
                                            ] || ''
                                        )}
                                        placeholder={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .LAST_NAME_PLACEHOLDER
                                            ] || ''
                                        )}
                                        value={personalDetail.lastName}
                                        onChange={onLastNameChange}
                                        onBlur={lastNameLabelHide}
                                        onFocus={lastNameLabelShow}
                                        errorMsg={lastNameErrorMsg}
                                        isLabelVisible={isLastNameLabelShow}
                                        maxLength={200}
                                    />
                                    <br></br>
                                    {displayCountryDialCodeComponent ? (
                                        <>
                                            <div className="osb-personal-details-country-dial-code">
                                                <label className="input-label">
                                                    <span>
                                                        {String(
                                                            personalDetailsJson[
                                                                PERSONAL_DETAIL_STEP_KEYS
                                                                    .COUNTRY_DIAL_CODE_LABEL
                                                            ] || ''
                                                        )}
                                                    </span>
                                                </label>
                                                <CountryDialCodePicker
                                                    countryDialCodeContent={
                                                        countryCodeData
                                                    }
                                                    displayDefaultMarketCode={
                                                        defaultMarketCodeToDisplay
                                                    }
                                                    selectedCountry={
                                                        selectedCountry
                                                    }
                                                />
                                            </div>
                                            <br></br>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                    <FormInput
                                        id="contactNumber"
                                        data-testid="EnterContactNumber"
                                        containerClassName="osb-personal-details-contact-number"
                                        label={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .CONTACT_NUMBER_LABEL
                                            ] || ''
                                        )}
                                        placeholder={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .CONTACT_NUMBER_PLACEHOLDER
                                            ] || ''
                                        )}
                                        value={personalDetail.contactNumber}
                                        onChange={onContactNumberChange}
                                        onBlur={contactNumberLabelHide}
                                        onFocus={contactNumberLabelShow}
                                        errorMsg={contactNumberErrorMsg}
                                        isLabelVisible={
                                            isContactNumberLabelShow
                                        }
                                    />
                                    <br></br>
                                    <FormInput
                                        id="email"
                                        data-testid="EnterEmail"
                                        containerClassName="osb-personal-details-email"
                                        label={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .EMAIL_LABEL
                                            ] || ''
                                        )}
                                        placeholder={String(
                                            personalDetailsJson[
                                                PERSONAL_DETAIL_STEP_KEYS
                                                    .EMAIL_PLACEHOLDER
                                            ] || ''
                                        )}
                                        value={personalDetail.email}
                                        onChange={onEmailChange}
                                        onBlur={emailLabelHide}
                                        onFocus={emailLabelShow}
                                        errorMsg={emailErrorMsg}
                                        isLabelVisible={isEmailLabelShow}
                                    />
                                    <br></br>
                                    {((!osbVehicleStep.vehicleDetails.vin &&
                                        !osbVehicleStep.vehicleDetails
                                            .registrationNumber) ||
                                        (osbStep.isRetrieveFlow &&
                                            osbVehicleStep.vehicleDetails
                                                .registrationNumber &&
                                            !osbVehicleStep.vehicleDetails
                                                .vin)) && (
                                        <>
                                            <FormInput
                                                id="registrationNumber"
                                                data-testid="EnterRegistrationNumber"
                                                containerClassName="osb-personal-details-registration-number"
                                                label={String(
                                                    personalDetailsJson[
                                                        PERSONAL_DETAIL_STEP_KEYS
                                                            .REG_NUMBER_LABEL
                                                    ] || ''
                                                )}
                                                placeholder={String(
                                                    personalDetailsJson[
                                                        PERSONAL_DETAIL_STEP_KEYS
                                                            .REG_NUMBER_PLACEHOLDER
                                                    ] || ''
                                                )}
                                                value={
                                                    personalDetail.registrationNumber
                                                }
                                                onChange={
                                                    onRegistrationNumberChange
                                                }
                                                onBlur={
                                                    registrationNumberLabelHide
                                                }
                                                onFocus={
                                                    registrationNumberLabelShow
                                                }
                                                errorMsg={
                                                    registrationNumberErrorMsg
                                                }
                                                isLabelVisible={
                                                    isregistrationNumberLabelShow
                                                }
                                            />
                                            <br></br>
                                        </>
                                    )}
                                    {showSaveToMyFordCheckbox && (
                                        <div className="osb-save-to-my-ford-account-container">
                                            <input
                                                data-testid="CheckboxToSaveUpdatedPersonalDetails"
                                                type="checkbox"
                                                onChange={
                                                    handleOsbSaveToMyFordChange
                                                }
                                                aria-label="checkbox"
                                                className="save-to-my-ford-account-checkbox"
                                            />
                                            <label className="osb-save-to-my-ford-account-label">
                                                {String(
                                                    personalDetailsJson[
                                                        PERSONAL_DETAIL_STEP_KEYS
                                                            .SAVE_TO_MY_FORD_LABEL
                                                    ] || ''
                                                )}
                                            </label>
                                        </div>
                                    )}
                                    {getSubHeading()}
                                    {(isAEMConsentEnabled(
                                        PersonalDetailsConsent.FIRST_CONSENT
                                    ) ||
                                        isAEMConsentEnabled(
                                            PersonalDetailsConsent.SECOND_CONSENT
                                        )) && (
                                        <div className="consent-checkbox-container">
                                            {isAEMConsentEnabled(
                                                PersonalDetailsConsent.FIRST_CONSENT
                                            ) && (
                                                <div className="consent-checkbox-section">
                                                    <label
                                                        className="consent-checkbox-label"
                                                        htmlFor="consent-checkbox-1"
                                                    >
                                                        <input
                                                            id="consent-checkbox-1"
                                                            data-testid="checkbox-1"
                                                            type="checkbox"
                                                            className="consent-checkbox"
                                                            placeholder="consent-checkbox-1"
                                                            name="consent-checkbox-1"
                                                            onChange={(
                                                                e: ChangeEvent<
                                                                    HTMLInputElement
                                                                >
                                                            ) =>
                                                                setComponentPayload(
                                                                    {
                                                                        isFirstConsentEnabled:
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                    }
                                                                )
                                                            }
                                                            aria-label="consent-checkbox-1"
                                                            defaultChecked={
                                                                false
                                                            }
                                                            disabled={
                                                                isFirstConsentDisabled
                                                            }
                                                        />
                                                        <span>
                                                            {String(
                                                                personalDetailsJson[
                                                                    PERSONAL_DETAIL_STEP_KEYS
                                                                        .CONSENT_FIRST_CHECKBOX_LABEL
                                                                ] || ''
                                                            )}
                                                            <a
                                                                href={String(
                                                                    personalDetailsJson[
                                                                        PERSONAL_DETAIL_STEP_KEYS
                                                                            .FIRST_CONSENT_PRIVACY_TARGET_URL
                                                                    ] || ''
                                                                )}
                                                                onClick={() =>
                                                                    handleConsentClick(
                                                                        PersonalDetailsConsent.FIRST_CONSENT
                                                                    )
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {' '}
                                                                {String(
                                                                    personalDetailsJson[
                                                                        PERSONAL_DETAIL_STEP_KEYS
                                                                            .FIRST_CONSENT_LINK_TEXT
                                                                    ] || ''
                                                                )}
                                                            </a>
                                                        </span>
                                                    </label>
                                                </div>
                                            )}
                                            {isAEMConsentEnabled(
                                                PersonalDetailsConsent.SECOND_CONSENT
                                            ) && (
                                                <div>
                                                    <label
                                                        className="consent-checkbox-label"
                                                        htmlFor="consent-checkbox-2"
                                                    >
                                                        <input
                                                            id="consent-checkbox-2"
                                                            data-testid="checkbox-2"
                                                            type="checkbox"
                                                            className="consent-checkbox"
                                                            placeholder="consent-checkbox-2"
                                                            name="consent-checkbox-2"
                                                            onChange={(
                                                                e: ChangeEvent<
                                                                    HTMLInputElement
                                                                >
                                                            ) =>
                                                                setComponentPayload(
                                                                    {
                                                                        isSecondConsentEnabled:
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                    }
                                                                )
                                                            }
                                                            aria-label="consent-checkbox-2"
                                                            defaultChecked={
                                                                false
                                                            }
                                                            disabled={
                                                                isSecondConsentDisabled
                                                            }
                                                        />
                                                        <span>
                                                            {String(
                                                                personalDetailsJson[
                                                                    PERSONAL_DETAIL_STEP_KEYS
                                                                        .CONSENT_SECOND_CHECKBOX_LABEL
                                                                ] || ''
                                                            )}
                                                            <a
                                                                href={String(
                                                                    personalDetailsJson[
                                                                        PERSONAL_DETAIL_STEP_KEYS
                                                                            .SECOND_CONSENT_PRIVACY_TARGET_URL
                                                                    ] || ''
                                                                )}
                                                                onClick={() =>
                                                                    handleConsentClick(
                                                                        PersonalDetailsConsent.SECOND_CONSENT
                                                                    )
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {' '}
                                                                {String(
                                                                    personalDetailsJson[
                                                                        PERSONAL_DETAIL_STEP_KEYS
                                                                            .SECOND_CONSENT_LINK_TEXT
                                                                    ] || ''
                                                                )}
                                                            </a>
                                                        </span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="button-container">
                                        <div className="second-button">
                                            <FMButton
                                                type="primary"
                                                label={String(
                                                    personalDetailsJson[
                                                        PERSONAL_DETAIL_STEP_KEYS
                                                            .SUBMIT_BUTTON
                                                    ] || ''
                                                )}
                                                disabled={
                                                    disableReviewBookingButtonValue
                                                }
                                                onClick={goToReviewBookingStep}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {!isMobileView && (
                            <div className="osb-light-journey-booking-summary">
                                <LightBookingSummary
                                    showVoucherLink={true}
                                    loadVoucherAlertMessage={
                                        loadVoucherAlertMessage
                                    }
                                />
                            </div>
                        )}
                    </div>
                    {isMobileView && (
                        <div className="osb-mobile-booking-summary">
                            <div className="view-summary-accordion">
                                <BookingSummaryAccordion
                                    index="0"
                                    className=""
                                    expandMultiplePanels={true}
                                    showVoucherLink={true}
                                    loadVoucherAlertMessage={
                                        loadVoucherAlertMessage
                                    }
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};
